import { useState } from 'react';
import { Loader2, Plus, Search, Tag } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import { TAG_PALETTE } from '@/features/dashboard/tags/types/tagColor.utils.ts';
import useGetTags from '@/features/dashboard/tags/hooks/useGetTags.ts';
import useGetTargetTags from '@/features/dashboard/tags/hooks/useGetTargetTags.ts';
import useAssignTag from '@/features/dashboard/tags/hooks/useAssignTag.ts';
import useUnassignTag from '@/features/dashboard/tags/hooks/useUnassignTag.ts';
import useCreateTag from '@/features/dashboard/tags/hooks/useCreateTag.ts';
import TagChip from '@/features/dashboard/tags/components/TagChip.tsx';

export interface TagPickerProps {
  targetType: TaggableType;
  targetId: string;
  /** Wywoływane po każdym przypisaniu, odpięciu i utworzeniu — odświeża tabelę. */
  onMutated?: () => void;
}

const TagPicker = ({ targetType, targetId, onMutated }: TagPickerProps) => {
  const [search, setSearch] = useState('');

  const { data: catalog = [], isLoading: catalogLoading } = useGetTags();
  const { data: assigned = [], isLoading: assignedLoading } = useGetTargetTags(
    targetType,
    targetId,
  );

  const assignMutation = useAssignTag(targetType, targetId, { onMutated });
  const unassignMutation = useUnassignTag(targetType, targetId, { onMutated });
  const createMutation = useCreateTag();

  const assignedIds = new Set(assigned.map((t) => t.id));
  const query = search.trim().toLowerCase();
  const available = catalog.filter(
    (t) => !assignedIds.has(t.id) && t.name.toLowerCase().includes(query),
  );
  const canCreate =
    query.length > 0 && !catalog.some((t) => t.name.toLowerCase() === query);

  const isBusy =
    assignMutation.isPending ||
    unassignMutation.isPending ||
    createMutation.isPending;
  const busyTagId = assignMutation.isPending
    ? assignMutation.variables
    : unassignMutation.isPending
      ? unassignMutation.variables
      : undefined;

  const handleCreate = async () => {
    const name = search.trim();
    if (!name) return;
    const color = TAG_PALETTE[catalog.length % TAG_PALETTE.length];
    const created = await createMutation.mutateAsync({ id: null, name, color });
    if (created.id && !assignedIds.has(created.id)) {
      assignMutation.mutate(created.id);
    }
    setSearch('');
  };

  if (catalogLoading || assignedLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-[18px] w-2/3" />
        <Skeleton className="h-[30px] w-full" />
        <Skeleton className="h-[18px] w-1/2" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {assigned.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {assigned.map((tag) => (
            <TagChip
              key={tag.id}
              tag={tag}
              onRemove={
                tag.id ? () => unassignMutation.mutate(tag.id!) : undefined
              }
            />
          ))}
        </div>
      ) : (
        <NoItems compact icon={<Tag />} title="Brak przypisanych tagów" />
      )}

      <div className="box-border flex h-[30px] items-center gap-1 rounded-md border border-neutral-200 px-2">
        <Search className="size-3.5 shrink-0 text-neutral-600" aria-hidden />
        <input
          type="text"
          className="w-full bg-transparent text-xs font-medium leading-[16px] text-base-text placeholder:text-neutral-300 focus:outline-none"
          placeholder="Szukaj lub utwórz tag"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canCreate) handleCreate();
            }
          }}
        />
      </div>

      <div className="flex max-h-44 flex-col gap-1 overflow-y-auto">
        {available.map((tag) => (
          <button
            key={tag.id}
            type="button"
            disabled={isBusy}
            onClick={() => tag.id && assignMutation.mutate(tag.id)}
            className="flex items-center justify-between gap-2 rounded-sm px-1 py-1 text-left hover:bg-neutral-50 disabled:opacity-50"
          >
            <TagChip tag={tag} />
            {busyTagId === tag.id ? (
              <Loader2 size={14} className="animate-spin text-neutral-400" />
            ) : (
              <Plus size={14} className="shrink-0 text-neutral-400" />
            )}
          </button>
        ))}

        {available.length === 0 && !canCreate && (
          <p className="px-1 py-1 text-xs text-neutral-300">
            {query
              ? 'Brak pasujących tagów'
              : 'Wszystkie tagi są już przypisane'}
          </p>
        )}
      </div>

      {canCreate && (
        <ActionButton
          tone="accent"
          size="toolbar"
          className="w-full"
          disabled={isBusy}
          isLoading={createMutation.isPending}
          onClick={handleCreate}
        >
          <Plus />
          <span className="truncate">Utwórz tag „{search.trim()}"</span>
        </ActionButton>
      )}
    </div>
  );
};

export default TagPicker;
